import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Snackbar,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  Grid,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SportsIcon from '@mui/icons-material/Sports';
import HistoryIcon from '@mui/icons-material/History';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ListIcon from '@mui/icons-material/List';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import PageLayout from './PageLayout';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ptBR from 'date-fns/locale/pt-BR';
import { format, startOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';

const VITALITY_COLOR = '#2E7D32';

const WORKOUT_TYPES = [
  { id: 'peito', label: 'Peito' },
  { id: 'costas', label: 'Costas' },
  { id: 'pernas', label: 'Pernas' },
  { id: 'ombro', label: 'Ombro' },
  { id: 'cardio', label: 'Cardio' },
  { id: 'full body', label: 'Full Body' },
];

function createEmptySet() {
  return { weight: 0, reps: 0 };
}

function createEmptyExercise() {
  return { name: '', muscleGroup: '', sets: [createEmptySet()] };
}

export default function Treinos({ customColors }) {
  const { currentUser } = useAuth();
  const primaryColor = customColors?.primary || VITALITY_COLOR;

  const [workoutEntries, setWorkoutEntries] = useState([]);
  const [customExercises, setCustomExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formDate, setFormDate] = useState(new Date());
  const [formWorkoutType, setFormWorkoutType] = useState('peito');
  const [formExercises, setFormExercises] = useState([createEmptyExercise()]);

  const [detailWorkout, setDetailWorkout] = useState(null);

  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseMuscleGroup, setNewExerciseMuscleGroup] = useState('');
  const [exerciseDialogOpen, setExerciseDialogOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) return;
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const snap = await getDoc(userRef);
        if (snap.exists()) {
          const data = snap.data();
          const entries = (data.workoutEntries || []).map((e) => ({
            ...e,
            id: e.id || `w-${e.date}-${Math.random().toString(36).slice(2)}`,
          }));
          entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          setWorkoutEntries(entries);
          setCustomExercises(data.customExercises || []);
        }
      } catch (err) {
        console.error('Erro ao carregar treinos:', err);
      }
    };
    load();
  }, [currentUser?.uid]);

  const saveCustomExercises = async (exercises) => {
    if (!currentUser?.uid) return;
    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, { customExercises: exercises });
    setCustomExercises(exercises);
  };

  const dashboardStats = useMemo(() => {
    const now = new Date();
    const monthStart = format(startOfMonth(now), 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');
    const thisMonth = workoutEntries.filter((w) => w.date >= monthStart && w.date <= monthEnd);

    let totalVolume = 0;
    const exerciseCount = {};
    workoutEntries.forEach((w) => {
      (w.exercises || []).forEach((ex) => {
        const vol = (ex.sets || []).reduce((s, set) => s + (Number(set.weight) || 0) * (Number(set.reps) || 0), 0);
        totalVolume += vol;
        const name = (ex.name || '').trim() || 'Sem nome';
        exerciseCount[name] = (exerciseCount[name] || 0) + 1;
      });
    });

    const sortedExercises = Object.entries(exerciseCount).sort((a, b) => b[1] - a[1]);
    const mostTrained = sortedExercises[0] ? sortedExercises[0][0] : '-';

    const dates = workoutEntries.map((w) => w.date).filter(Boolean);
    let streak = 0;
    for (let i = 0; i < 365; i++) {
      const d = format(subDays(now, i), 'yyyy-MM-dd');
      if (dates.includes(d)) streak++;
      else break;
    }

    return {
      countThisMonth: thisMonth.length,
      totalVolume: Math.round(totalVolume),
      streak,
      mostTrained,
    };
  }, [workoutEntries]);

  const chartDataByWeek = useMemo(() => {
    const byWeek = {};
    workoutEntries.forEach((w) => {
      if (!w.date) return;
      const d = new Date(w.date + 'T12:00:00');
      const weekStart = format(startOfWeek(d, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      byWeek[weekStart] = (byWeek[weekStart] || 0) + 1;
    });
    const weeks = Object.keys(byWeek).sort();
    const lastWeeks = weeks.slice(-8);
    return lastWeeks.map((w) => ({
      week: format(new Date(w + 'T12:00:00'), 'dd/MM', { locale: ptBR }),
      treinos: byWeek[w] || 0,
    }));
  }, [workoutEntries]);

  const evolutionVolumeByWeek = useMemo(() => {
    const byWeek = {};
    workoutEntries.forEach((w) => {
      (w.exercises || []).forEach((ex) => {
        (ex.sets || []).forEach((set) => {
          const vol = (Number(set.weight) || 0) * (Number(set.reps) || 0);
          if (!w.date) return;
          const d = new Date(w.date + 'T12:00:00');
          const weekStart = format(startOfWeek(d, { weekStartsOn: 0 }), 'yyyy-MM-dd');
          byWeek[weekStart] = (byWeek[weekStart] || 0) + vol;
        });
      });
    });
    const weeks = Object.keys(byWeek).sort().slice(-12);
    return weeks.map((w) => ({
      week: format(new Date(w + 'T12:00:00'), 'dd/MM', { locale: ptBR }),
      volume: Math.round(byWeek[w] || 0),
    }));
  }, [workoutEntries]);

  const evolutionFrequencyByWeek = useMemo(() => {
    const byWeek = {};
    workoutEntries.forEach((w) => {
      if (!w.date) return;
      const d = new Date(w.date + 'T12:00:00');
      const weekStart = format(startOfWeek(d, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      byWeek[weekStart] = (byWeek[weekStart] || 0) + 1;
    });
    const weeks = Object.keys(byWeek).sort().slice(-12);
    return weeks.map((w) => ({
      week: format(new Date(w + 'T12:00:00'), 'dd/MM', { locale: ptBR }),
      treinos: byWeek[w] || 0,
    }));
  }, [workoutEntries]);

  const evolutionByExercise = useMemo(() => {
    const byExercise = {};
    workoutEntries.forEach((w) => {
      (w.exercises || []).forEach((ex) => {
        const name = (ex.name || '').trim() || 'Sem nome';
        if (!byExercise[name]) byExercise[name] = [];
        const maxLoad = Math.max(
          ...(ex.sets || []).map((s) => Number(s.weight) || 0),
          0
        );
        if (w.date && maxLoad > 0) {
          byExercise[name].push({
            date: w.date,
            label: format(new Date(w.date + 'T12:00:00'), 'dd/MM', { locale: ptBR }),
            cargaMax: maxLoad,
          });
        }
      });
    });
    Object.keys(byExercise).forEach((k) => {
      byExercise[k].sort((a, b) => a.date.localeCompare(b.date));
    });
    return byExercise;
  }, [workoutEntries]);

  const handleAddExerciseInForm = () => {
    setFormExercises((prev) => [...prev, createEmptyExercise()]);
  };

  const handleRemoveExerciseInForm = (index) => {
    if (formExercises.length <= 1) return;
    setFormExercises((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateExerciseInForm = (index, field, value) => {
    setFormExercises((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleUpdateSetInForm = (exIndex, setIndex, field, value) => {
    setFormExercises((prev) => {
      const next = [...prev];
      const ex = next[exIndex];
      const sets = [...(ex.sets || [])];
      sets[setIndex] = { ...sets[setIndex], [field]: field === 'weight' ? Number(value) || 0 : Number(value) || 0 };
      next[exIndex] = { ...ex, sets };
      return next;
    });
  };

  const handleAddSetInForm = (exIndex) => {
    setFormExercises((prev) => {
      const next = [...prev];
      const ex = next[exIndex];
      next[exIndex] = { ...ex, sets: [...(ex.sets || []), createEmptySet()] };
      return next;
    });
  };

  const handleRemoveSetInForm = (exIndex, setIndex) => {
    setFormExercises((prev) => {
      const next = [...prev];
      const ex = next[exIndex];
      const sets = (ex.sets || []).filter((_, i) => i !== setIndex);
      if (sets.length === 0) sets.push(createEmptySet());
      next[exIndex] = { ...ex, sets };
      return next;
    });
  };

  const handleSaveWorkout = async () => {
    if (!currentUser?.uid) return;
    const dateStr = format(formDate, 'yyyy-MM-dd');
    const exercises = formExercises
      .map((ex) => ({
        name: (ex.name || '').trim(),
        muscleGroup: (ex.muscleGroup || '').trim(),
        sets: (ex.sets || []).map((s) => ({
          weight: Number(s.weight) || 0,
          reps: Number(s.reps) || 0,
        })),
      }))
      .filter((ex) => ex.name || (ex.sets || []).some((s) => s.weight > 0 || s.reps > 0));

    if (exercises.length === 0) {
      setSnackbar({ open: true, message: 'Adicione pelo menos um exercício.', severity: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const current = (snap.data()?.workoutEntries || []);
      const newEntry = {
        id: `w-${dateStr}-${Date.now()}`,
        date: dateStr,
        workoutType: formWorkoutType,
        exercises,
        updatedAt: new Date().toISOString(),
      };
      const updated = [...current, newEntry].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      await updateDoc(userRef, { workoutEntries: updated });
      setWorkoutEntries(updated);
      setFormDate(new Date());
      setFormWorkoutType('peito');
      setFormExercises([createEmptyExercise()]);
      setSnackbar({ open: true, message: 'Treino registrado com sucesso!', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Erro ao salvar. Tente novamente.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkout = async (entry) => {
    if (!currentUser?.uid || !window.confirm('Excluir este treino?')) return;
    setLoading(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const snap = await getDoc(userRef);
      const current = snap.data()?.workoutEntries || [];
      const updated = current.filter((e) => e.id !== entry.id);
      await updateDoc(userRef, { workoutEntries: updated });
      setWorkoutEntries([...updated].sort((a, b) => (b.date || '').localeCompare(a.date || '')));
      if (detailWorkout?.id === entry.id) setDetailWorkout(null);
      setSnackbar({ open: true, message: 'Treino excluído.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao excluir.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomExercise = async () => {
    const name = (newExerciseName || '').trim();
    const muscleGroup = (newExerciseMuscleGroup || '').trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Nome do exercício é obrigatório.', severity: 'warning' });
      return;
    }
    const newEx = {
      id: `ex-${Date.now()}`,
      name,
      muscleGroup,
      createdAt: new Date().toISOString(),
    };
    const updated = [...customExercises, newEx];
    await saveCustomExercises(updated);
    setNewExerciseName('');
    setNewExerciseMuscleGroup('');
    setExerciseDialogOpen(false);
    setSnackbar({ open: true, message: 'Exercício adicionado!', severity: 'success' });
  };

  const handleDeleteCustomExercise = async (ex) => {
    if (!window.confirm(`Remover "${ex.name}" da lista?`)) return;
    const updated = customExercises.filter((e) => e.id !== ex.id);
    await saveCustomExercises(updated);
    setSnackbar({ open: true, message: 'Exercício removido.', severity: 'success' });
  };

  const allExerciseNames = useMemo(() => {
    const set = new Set();
    customExercises.forEach((e) => set.add(e.name));
    workoutEntries.forEach((w) => (w.exercises || []).forEach((ex) => ex.name && set.add(ex.name)));
    return Array.from(set).sort();
  }, [customExercises, workoutEntries]);

  return (
    <PageLayout title="Treinos" customColors={customColors} hideAddButton>
      <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
        <Card
          sx={{
            mb: 2,
            background: `linear-gradient(135deg, ${alpha(primaryColor, 0.12)} 0%, ${alpha(primaryColor, 0.04)} 100%)`,
            borderLeft: `4px solid ${primaryColor}`,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontWeight: 600 },
              '& .Mui-selected': { color: primaryColor },
              '& .MuiTabs-indicator': { backgroundColor: primaryColor },
            }}
          >
            <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Dashboard" />
            <Tab icon={<AddIcon />} iconPosition="start" label="Registrar treino" />
            <Tab icon={<HistoryIcon />} iconPosition="start" label="Histórico" />
            <Tab icon={<ShowChartIcon />} iconPosition="start" label="Evolução" />
            <Tab icon={<ListIcon />} iconPosition="start" label="Exercícios" />
          </Tabs>
        </Card>

        {activeTab === 0 && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card sx={{ borderTop: `3px solid ${primaryColor}`, bgcolor: alpha(primaryColor, 0.06), height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <FitnessCenterIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">Treinos no mês</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>{dashboardStats.countThisMonth}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ borderTop: `3px solid ${primaryColor}`, bgcolor: alpha(primaryColor, 0.06), height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <TrendingUpIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">Volume total (kg)</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>{dashboardStats.totalVolume}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ borderTop: `3px solid ${primaryColor}`, bgcolor: alpha(primaryColor, 0.06), height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <LocalFireDepartmentIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">Streak (dias)</Typography>
                    </Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: primaryColor }}>{dashboardStats.streak}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ borderTop: `3px solid ${primaryColor}`, bgcolor: alpha(primaryColor, 0.06), height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <SportsIcon sx={{ color: primaryColor }} />
                      <Typography variant="caption" color="text.secondary">Mais treinado</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }} noWrap>{dashboardStats.mostTrained}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Card sx={{ borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>Treinos por semana</Typography>
                {chartDataByWeek.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Registre treinos para ver o gráfico.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartDataByWeek} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="treinos" fill={primaryColor} name="Treinos" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {activeTab === 1 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>Registrar treino</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                    <DatePicker
                      label="Data"
                      value={formDate}
                      onChange={(d) => setFormDate(d || new Date())}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Tipo de treino"
                    value={formWorkoutType}
                    onChange={(e) => setFormWorkoutType(e.target.value)}
                    SelectProps={{ native: true }}
                  >
                    {WORKOUT_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>Exercícios</Typography>
              {formExercises.map((ex, exIndex) => (
                <Card key={exIndex} variant="outlined" sx={{ mb: 2, borderColor: alpha(primaryColor, 0.3) }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <TextField
                        label="Nome do exercício"
                        size="small"
                        value={ex.name}
                        onChange={(e) => handleUpdateExerciseInForm(exIndex, 'name', e.target.value)}
                        sx={{ minWidth: 200 }}
                        list={`ex-list-${exIndex}`}
                      />
                      <datalist id={`ex-list-${exIndex}`}>
                        {allExerciseNames.map((n) => (
                          <option key={n} value={n} />
                        ))}
                      </datalist>
                      <TextField
                        label="Grupo muscular"
                        size="small"
                        value={ex.muscleGroup}
                        onChange={(e) => handleUpdateExerciseInForm(exIndex, 'muscleGroup', e.target.value)}
                        sx={{ minWidth: 140 }}
                      />
                      <IconButton size="small" color="error" onClick={() => handleRemoveExerciseInForm(exIndex)} disabled={formExercises.length <= 1}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 400 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Peso (kg)</TableCell>
                            <TableCell>Reps</TableCell>
                            <TableCell align="right">Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(ex.sets || []).map((set, setIndex) => (
                            <TableRow key={setIndex}>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={set.weight || ''}
                                  onChange={(e) => handleUpdateSetInForm(exIndex, setIndex, 'weight', e.target.value)}
                                  inputProps={{ min: 0, step: 0.5 }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={set.reps || ''}
                                  onChange={(e) => handleUpdateSetInForm(exIndex, setIndex, 'reps', e.target.value)}
                                  inputProps={{ min: 0 }}
                                  sx={{ width: 70 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveSetInForm(exIndex, setIndex)}
                                  disabled={(ex.sets || []).length <= 1}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddSetInForm(exIndex)} sx={{ mt: 1, color: primaryColor }}>
                      Adicionar série
                    </Button>
                  </CardContent>
                </Card>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddExerciseInForm} sx={{ borderColor: primaryColor, color: primaryColor }}>
                  Adicionar exercício
                </Button>
                <Button variant="contained" onClick={handleSaveWorkout} disabled={loading} sx={{ bgcolor: primaryColor }}>
                  {loading ? 'Salvando...' : 'Salvar treino'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor, mb: 2 }}>Histórico de treinos</Typography>
              {workoutEntries.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum treino registrado ainda.</Typography>
              ) : (
                <List dense>
                  {workoutEntries.map((entry) => (
                    <ListItem
                      key={entry.id}
                      button
                      onClick={() => setDetailWorkout(entry)}
                      sx={{
                        borderLeft: `3px solid ${primaryColor}`,
                        mb: 0.5,
                        bgcolor: alpha(primaryColor, 0.04),
                        borderRadius: 1,
                      }}
                    >
                      <ListItemText
                        primary={`${entry.date ? format(new Date(entry.date + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR }) : ''} — ${WORKOUT_TYPES.find((t) => t.id === entry.workoutType)?.label || entry.workoutType}`}
                        secondary={`${(entry.exercises || []).length} exercício(s)`}
                      />
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteWorkout(entry); }}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 3 && (
          <Box sx={{ mt: 2 }}>
            <Card sx={{ mb: 2, borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>Volume semanal (kg)</Typography>
                {evolutionVolumeByWeek.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Sem dados ainda.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={evolutionVolumeByWeek} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="volume" stroke={primaryColor} name="Volume" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card sx={{ mb: 2, borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>Frequência de treino por semana</Typography>
                {evolutionFrequencyByWeek.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Sem dados ainda.</Typography>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={evolutionFrequencyByWeek} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="treinos" fill={primaryColor} name="Treinos" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card sx={{ borderLeft: `4px solid ${primaryColor}` }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: primaryColor }}>Progresso de carga por exercício</Typography>
                {Object.keys(evolutionByExercise).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Registre treinos com peso para ver a evolução.</Typography>
                ) : (
                  Object.entries(evolutionByExercise)
                    .filter(([, points]) => points.length > 0)
                    .map(([exerciseName, points]) => (
                      <Box key={exerciseName} sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: primaryColor, mb: 1 }}>{exerciseName}</Typography>
                        <ResponsiveContainer width="100%" height={180}>
                          <LineChart data={points} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                            <YAxis />
                            <Tooltip />
                            <Line type="monotone" dataKey="cargaMax" stroke={primaryColor} name="Carga máx (kg)" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ))
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {activeTab === 4 && (
          <Card sx={{ mt: 2, borderLeft: `4px solid ${primaryColor}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: primaryColor }}>Exercícios cadastrados</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setExerciseDialogOpen(true)} sx={{ bgcolor: primaryColor }}>
                  Novo exercício
                </Button>
              </Box>
              {customExercises.length === 0 && workoutEntries.every((w) => !(w.exercises || []).length) ? (
                <Typography variant="body2" color="text.secondary">Nenhum exercício ainda. Adicione um treino ou cadastre um exercício personalizado.</Typography>
              ) : (
                <List dense>
                  {customExercises.map((ex) => (
                    <ListItem
                      key={ex.id}
                      secondaryAction={
                        <IconButton edge="end" size="small" color="error" onClick={() => handleDeleteCustomExercise(ex)}>
                          <DeleteIcon />
                        </IconButton>
                      }
                      sx={{ borderLeft: `3px solid ${alpha(primaryColor, 0.5)}`, mb: 0.5, borderRadius: 1 }}
                    >
                      <ListItemText primary={ex.name} secondary={ex.muscleGroup || '—'} />
                    </ListItem>
                  ))}
                  {customExercises.length === 0 && allExerciseNames.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Exercícios usados nos treinos: {allExerciseNames.join(', ')}. Cadastre como personalizados para reutilizar no formulário.
                    </Typography>
                  )}
                </List>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={!!detailWorkout} onClose={() => setDetailWorkout(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ borderBottom: `2px solid ${primaryColor}`, color: primaryColor }}>
            {detailWorkout && (
              <>
                {format(new Date(detailWorkout.date + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR })} — {WORKOUT_TYPES.find((t) => t.id === detailWorkout.workoutType)?.label || detailWorkout.workoutType}
              </>
            )}
          </DialogTitle>
          <DialogContent>
            {detailWorkout && (detailWorkout.exercises || []).map((ex, i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: primaryColor }}>{ex.name || 'Sem nome'}</Typography>
                {ex.muscleGroup && <Typography variant="caption" color="text.secondary">{ex.muscleGroup}</Typography>}
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Peso (kg)</TableCell>
                        <TableCell>Reps</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(ex.sets || []).map((set, j) => (
                        <TableRow key={j}>
                          <TableCell>{set.weight}</TableCell>
                          <TableCell>{set.reps}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailWorkout(null)}>Fechar</Button>
            {detailWorkout && (
              <Button color="error" onClick={() => { handleDeleteWorkout(detailWorkout); setDetailWorkout(null); }}>Excluir treino</Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog open={exerciseDialogOpen} onClose={() => setExerciseDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ color: primaryColor }}>Novo exercício</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Nome do exercício"
              value={newExerciseName}
              onChange={(e) => setNewExerciseName(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              fullWidth
              label="Grupo muscular"
              value={newExerciseMuscleGroup}
              onChange={(e) => setNewExerciseMuscleGroup(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExerciseDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleAddCustomExercise} sx={{ bgcolor: primaryColor }}>Adicionar</Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}
